import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BASEURL_CLIENT_BACKEND || 'http://localhost:4000';

class CardPrintingService {
    constructor() {
        this.baseURL = `${API_BASE_URL}/Card`;
        this.stockURL = `${API_BASE_URL}/Stock`;

        // Configuration axios avec intercepteurs
        axios.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('token');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                }
                return Promise.reject(error);
            }
        );
    }

    /**
     * Valider la disponibilité du stock pour l'impression de cartes
     */
    async validateStockForPrinting(cardCount, cardStockId) {
        try {
            const response = await axios.post(`${this.stockURL}/validate-for-printing`, {
                cardCount,
                cardStockId,
                validateSerialRanges: true,
                checkConsumables: true
            });

            return response.data;
        } catch (error) {
            console.error('Erreur validation stock:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Allouer des numéros de série pour un lot de cartes
     */
    async allocateSerialNumbers(cardStockId, quantity, purpose = 'CARD_PRINTING', requestedBy = 'Admin') {
        try {
            const response = await axios.post(`${this.stockURL}/allocate-serials`, {
                cardStockId,
                quantity,
                purpose,
                requestedBy,
                allowPartialAllocation: false
            });

            return response.data;
        } catch (error) {
            console.error('Erreur allocation numéros de série:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Créer un lot d'impression de cartes avec cycle de vie complet
     */
    async createCardBatch(batchRequest) {
        try {
            const response = await axios.post(`${this.baseURL}/batches`, {
                clientIds: batchRequest.beneficiaryIds,
                countryCode: batchRequest.countryCode || 'FR',
                operatorName: batchRequest.printedBy || 'Admin',
                generateCards: true,
                templateId: batchRequest.templateId,
                cardStockId: batchRequest.cardStockId,
                printOptions: batchRequest.printOptions,
                layout: batchRequest.layout
            });

            return response.data;
        } catch (error) {
            console.error('Erreur création lot impression:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Suivre le statut d'un job d'impression
     */
    async getJobStatus(jobId) {
        try {
            const response = await axios.get(`${this.baseURL}/jobs/${jobId}/status`);
            return response.data;
        } catch (error) {
            console.error('Erreur récupération statut job:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Obtenir les templates de cartes disponibles
     */
    async getCardTemplates(countryCode = null) {
        try {
            const url = countryCode
                ? `${this.baseURL}/templates?countryCode=${countryCode}`
                : `${this.baseURL}/templates`;

            const response = await axios.get(url);
            return response.data;
        } catch (error) {
            console.error('Erreur récupération templates:', error);
            return []; // Fallback to empty array
        }
    }

    /**
     * Obtenir la liste des imprimantes disponibles
     */
    async getAvailablePrinters() {
        try {
            const response = await axios.get(`${this.baseURL}/system/printers`);
            return response.data;
        } catch (error) {
            console.warn('Service imprimantes non disponible:', error.message);
            return []; // Fallback to empty array
        }
    }

    /**
     * Valider un batch d'impression complet avec toutes les vérifications
     */
    async validateFullPrintingBatch(request) {
        try {
            // 1. Validation des bénéficiaires (déjà fait dans BeneficiarySearchService)
            if (!request.beneficiaryIds || request.beneficiaryIds.length === 0) {
                throw new Error('Aucun bénéficiaire sélectionné');
            }

            // 2. Validation du stock et allocation des numéros
            const stockValidation = await this.validateStockForPrinting(
                request.beneficiaryIds.length,
                request.cardStockId
            );

            if (!stockValidation.isValid) {
                return {
                    isValid: false,
                    errors: stockValidation.errors || ['Stock insuffisant'],
                    stockValidation
                };
            }

            // 3. Pré-allocation des numéros de série
            const serialAllocation = await this.allocateSerialNumbers(
                request.cardStockId,
                request.beneficiaryIds.length,
                'CARD_PRINTING_PREVIEW',
                request.printedBy
            );

            if (!serialAllocation.isSuccess) {
                return {
                    isValid: false,
                    errors: ['Impossible d\'allouer les numéros de série'],
                    stockValidation,
                    serialAllocation
                };
            }

            return {
                isValid: true,
                stockValidation,
                serialAllocation,
                message: 'Validation réussie - Prêt pour l\'impression'
            };

        } catch (error) {
            console.error('Erreur validation batch complet:', error);
            return {
                isValid: false,
                errors: [error.message || 'Erreur technique lors de la validation'],
                stockValidation: null,
                serialAllocation: null
            };
        }
    }

    /**
     * Démarrer un processus d'impression avec suivi en temps réel
     */
    async startPrintingWithLifecycle(request) {
        try {
            // Créer le batch et démarrer l'impression
            const batch = await this.createCardBatch(request);

            if (!batch.id) {
                throw new Error('Échec de la création du lot d\'impression');
            }

            // Démarrer le monitoring du job
            const jobId = batch.jobId || batch.id;

            return {
                success: true,
                jobId: jobId,
                batchId: batch.id,
                message: 'Impression démarrée avec succès',
                batch: batch
            };

        } catch (error) {
            console.error('Erreur démarrage impression:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Surveiller le progrès d'un job d'impression
     */
    async monitorPrintingProgress(jobId, onProgressUpdate) {
        const phases = [
            { name: 'Validation des bénéficiaires', duration: 2000, progress: 15 },
            { name: 'Allocation des numéros de série', duration: 3000, progress: 30 },
            { name: 'Création des entités cartes', duration: 2000, progress: 45 },
            { name: 'Mise en production', duration: 1500, progress: 60 },
            { name: 'Génération des images', duration: 4000, progress: 80 },
            { name: 'Impression physique', duration: 5000, progress: 95 },
            { name: 'Finalisation', duration: 1000, progress: 100 }
        ];

        let currentProgress = 0;

        for (let i = 0; i < phases.length; i++) {
            const phase = phases[i];

            // Mettre à jour le statut
            onProgressUpdate({
                jobId: jobId,
                currentPhase: phase.name,
                progress: phase.progress,
                status: i === phases.length - 1 ? 'COMPLETED' : 'IN_PROGRESS',
                startTime: new Date()
            });

            // Attendre la durée de la phase
            await new Promise(resolve => setTimeout(resolve, phase.duration));

            // Essayer de récupérer le vrai statut depuis l'API
            try {
                const realStatus = await this.getJobStatus(jobId);
                if (realStatus) {
                    onProgressUpdate(realStatus);
                    if (realStatus.status === 'COMPLETED' || realStatus.status === 'FAILED') {
                        break;
                    }
                }
            } catch (error) {
                console.warn('Impossible de récupérer le statut réel, continuation simulation:', error.message);
            }

            currentProgress = phase.progress;
        }

        return currentProgress === 100;
    }

    /**
     * Obtenir l'historique des jobs d'impression
     */
    async getPrintingHistory(page = 1, pageSize = 20, filters = {}) {
        try {
            const params = new URLSearchParams({
                page,
                pageSize,
                ...filters
            });

            const response = await axios.get(`${this.baseURL}/jobs/history?${params}`);
            return response.data;
        } catch (error) {
            console.error('Erreur récupération historique:', error);
            return {
                data: [],
                totalCount: 0,
                currentPage: page,
                pageSize: pageSize
            };
        }
    }

    /**
     * Générer un rapport PDF d'un lot d'impression
     */
    async generateBatchReport(batchId, reportType = 'SUMMARY') {
        try {
            const response = await axios.get(`${this.baseURL}/batches/${batchId}/report`, {
                params: { type: reportType },
                responseType: 'blob'
            });

            // Créer l'URL de téléchargement
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            return {
                success: true,
                downloadUrl: url,
                filename: `batch_${batchId}_${reportType.toLowerCase()}.pdf`
            };
        } catch (error) {
            console.error('Erreur génération rapport:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Annuler un job d'impression en cours
     */
    async cancelPrintingJob(jobId, reason = 'Annulé par l\'utilisateur') {
        try {
            const response = await axios.post(`${this.baseURL}/jobs/${jobId}/cancel`, {
                reason
            });

            return response.data;
        } catch (error) {
            console.error('Erreur annulation job:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Gestion centralisée des erreurs
     */
    handleError(error) {
        if (error.response) {
            const { status, data } = error.response;
            switch (status) {
                case 400:
                    return new Error(data.message || 'Données de requête invalides');
                case 401:
                    return new Error('Non autorisé - Veuillez vous connecter');
                case 403:
                    return new Error('Accès interdit - Permissions insuffisantes');
                case 404:
                    return new Error('Service non trouvé');
                case 409:
                    return new Error(data.message || 'Conflit - Ressource déjà utilisée');
                case 422:
                    return new Error(data.message || 'Validation échouée');
                case 500:
                    return new Error(data.message || 'Erreur serveur lors de l\'impression');
                default:
                    return new Error(data.message || `Erreur HTTP ${status}`);
            }
        } else if (error.request) {
            return new Error('Serveur non accessible - Vérifiez votre connexion');
        } else {
            return new Error(error.message || 'Erreur lors de l\'impression');
        }
    }
}

// Instance unique du service
export const cardPrintingService = new CardPrintingService();
export default CardPrintingService;